import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import NotesIcon from '@mui/icons-material/Notes';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import type { IIssueWorkLog, ITimesheetProjectApproval, ITimesheetTaskRow } from '@pulse/shared';
import { Alert, Box, Button, ICON_SIZE, IconButton, LabelWithIcon, Paper, Stack, TextField, Tooltip, Typography } from '@pulse/ui';
import { useState } from 'react';

import { LINE_CLAMP_2_SX, TruncatableText } from '../../../components/TruncatableText';
import { useAuth } from '../../../context/AuthContext';
import { formatDdMmYyyy } from '../../../lib/date.util';
import { blockNegativeHourKeys, isValidHours, restrictedHoursChange } from '../../../lib/hours-input.util';
import { isEditableWorkLogDate } from '../../../lib/work-log-edit-window.util';
import { useAddIssueWorkLog, useUpdateIssueWorkLog } from '../../delivery/hooks/useIssues';
import { TEXT_SCROLL_4_LINES_SX, entriesForCell } from '../utils/timesheetGrid';

// Matches CreateIssueWorkLogDto's @MaxLength(5000) on description (apps/api/src/modules/presales/dto/create-issue-work-log.dto.ts).
const WORK_LOG_DESCRIPTION_MAX = 5000;
// A single work log entry can't exceed a full day.
const HOURS_MAX = 24;

interface WorkLogPanelProps {
  taskRows: ITimesheetTaskRow[];
  workLogs: IIssueWorkLog[];
  selectedCell: { issueId: string; dateIso: string };
  /** Per-project approval status for this week (see ITimesheetWeekResponse.projectApprovals). */
  projectApprovals?: ITimesheetProjectApproval[];
  onClose: () => void;
  onError: (message: string) => void;
  /** Closes the panel right after a successful "Add" instead of clearing the form and staying
   *  open — set by callers that render this inside a Dialog (mobile/tablet), where the panel
   *  is a one-shot modal rather than a persistent side panel the user adds several entries
   *  into. Desktop's inline side panel omits this and keeps its stay-open behavior. */
  closeOnAdd?: boolean;
}

// Right-side panel for a clicked (task, day) cell — lists that cell's individual work log
// entries with inline edit, plus an "Add entry" form. No delete: the backend has no DELETE
// endpoint for work logs today and none is being added here. The Timesheet week query (and
// Home's issue/time-summary queries) are invalidated centrally by useAddIssueWorkLog/
// useUpdateIssueWorkLog themselves (see invalidateWorkLogQueries in delivery/hooks/useIssues.ts).
export function WorkLogPanel({ taskRows, workLogs, selectedCell, projectApprovals = [], onClose, onError, closeOnAdd }: WorkLogPanelProps) {
  const { user } = useAuth();
  const task = taskRows.find((t) => t.issueId === selectedCell.issueId);
  const entries = entriesForCell(workLogs, selectedCell.issueId, selectedCell.dateIso);
  const approval = task ? projectApprovals.find((a) => a.projectId === task.projectId) : undefined;
  // Rejected does NOT lock — it's feedback the member can act on; Submitted (awaiting review)
  // and Approved both do.
  const isProjectLocked = approval?.status === 'Submitted' || approval?.status === 'Approved';
  const cellIsEditable = isEditableWorkLogDate(selectedCell.dateIso) && !isProjectLocked;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editHours, setEditHours] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const editDescriptionOverLimit = editDescription.length > WORK_LOG_DESCRIPTION_MAX;
  const editHoursOverMax = Number(editHours) > HOURS_MAX;
  // The day's running total (across ALL tasks, not just this cell's) is not re-derivable
  // client-side — entries also come in from IssueDetailDialog/IssueLinkPage/LogTimeDialog, so
  // any local total this panel computes can be stale relative to what's actually been saved.
  // That check is enforced server-side instead (DailyHoursWithinLimit in IssueService).

  const [addHours, setAddHours] = useState('');
  const [addDescription, setAddDescription] = useState('');
  const addDescriptionOverLimit = addDescription.length > WORK_LOG_DESCRIPTION_MAX;
  const addHoursOverMax = Number(addHours) > HOURS_MAX;

  const addMutation = useAddIssueWorkLog();
  const updateMutation = useUpdateIssueWorkLog();

  function startEdit(entry: IIssueWorkLog) {
    setEditingId(entry.id);
    setEditHours(String(entry.hours));
    setEditDescription(entry.description ?? '');
  }

  function saveEdit(entry: IIssueWorkLog) {
    if (!task) return;
    updateMutation.mutate(
      {
        projectId: task.projectId,
        issueId: task.issueId,
        workLogId: entry.id,
        data: { hours: Number(editHours), description: editDescription.trim() || null },
      },
      {
        onSuccess: () => setEditingId(null),
        onError: (err: Error) => onError(err.message),
      },
    );
  }

  function addEntry() {
    if (!task) return;
    addMutation.mutate(
      {
        projectId: task.projectId,
        issueId: task.issueId,
        data: { hours: Number(addHours), date: selectedCell.dateIso, description: addDescription.trim() || undefined },
      },
      {
        onSuccess: () => {
          setAddHours('');
          setAddDescription('');
          if (closeOnAdd) onClose();
        },
        onError: (err: Error) => onError(err.message),
      },
    );
  }

  return (
    <Paper sx={{ p: 2, width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>Work Logs</Typography>
          <TruncatableText
            text={task ? `${task.issueKey} — ${task.issueName}` : ''}
            variant="caption"
            color="text.secondary"
            placement="top"
            sx={LINE_CLAMP_2_SX}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {formatDdMmYyyy(selectedCell.dateIso)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Stack>

      {approval?.status === 'Rejected' && (
        <Alert severity="warning" icon={<ThumbDownIcon fontSize="inherit" />} sx={{ mb: 1.5 }}>
          <Box sx={TEXT_SCROLL_4_LINES_SX}>
            This project&apos;s entries for this week were rejected{approval.rejectionReason ? `: ${approval.rejectionReason}` : '.'}
          </Box>
        </Alert>
      )}

      {!cellIsEditable && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
          <LockIcon fontSize="small" color="disabled" />
          <Typography variant="caption" color="text.secondary">
            {approval?.status === 'Submitted'
              ? 'This project\'s entries for this week have been submitted and are awaiting review.'
              : approval?.status === 'Approved'
                ? 'This project\'s entries for this week have been approved and locked.'
                : 'This date is outside the current/previous week and is read-only.'}
          </Typography>
        </Stack>
      )}

      <Stack spacing={1}>
        {entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">No entries for this day yet.</Typography>
        )}
        {entries.map((entry) => {
          const canEdit = cellIsEditable && entry.author.id === user?.id;
          return (
            <Stack key={entry.id} sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} spacing={0.5}>
              {editingId === entry.id ? (
                <Stack spacing={1}>
                  <TextField
                    label={<LabelWithIcon icon={<AccessTimeIcon fontSize={ICON_SIZE.sm} />} label="Hours" inline />}
                    type="number"
                    size="small"
                    inputProps={{ min: 0, max: HOURS_MAX, step: 'any' }}
                    value={editHours}
                    onChange={restrictedHoursChange(setEditHours)}
                    onKeyDown={blockNegativeHourKeys}
                    error={editHoursOverMax}
                    helperText={editHoursOverMax ? 'Hours cannot exceed 24h' : ' '}
                  />
                  <TextField
                    label={<LabelWithIcon icon={<NotesIcon fontSize={ICON_SIZE.sm} />} label="Description" inline />}
                    size="small"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    error={editDescriptionOverLimit}
                    helperText={
                      editDescriptionOverLimit
                        ? `${editDescription.length}/${WORK_LOG_DESCRIPTION_MAX} — ${editDescription.length - WORK_LOG_DESCRIPTION_MAX} characters over the limit`
                        : `${editDescription.length}/${WORK_LOG_DESCRIPTION_MAX}`
                    }
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                    <Button size="small" variant="contained" disabled={!isValidHours(editHours) || editHoursOverMax || editDescriptionOverLimit} onClick={() => saveEdit(entry)}>Save</Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{entry.hours}h — {entry.author.name}</Typography>
                    {entry.description && <Typography variant="caption" color="text.secondary" display="block" sx={TEXT_SCROLL_4_LINES_SX}>{entry.description}</Typography>}
                  </Box>
                  {canEdit && (
                    <IconButton size="small" onClick={() => startEdit(entry)}><EditIcon fontSize="small" /></IconButton>
                  )}
                </Stack>
              )}
            </Stack>
          );
        })}
      </Stack>

      {cellIsEditable ? (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="caption" fontWeight={700}>Add entry</Typography>
          <TextField
            label={<LabelWithIcon icon={<AccessTimeIcon fontSize={ICON_SIZE.sm} />} label="Hours" inline />}
            type="number"
            size="small"
            inputProps={{ min: 0, max: HOURS_MAX, step: 'any' }}
            value={addHours}
            onChange={restrictedHoursChange(setAddHours)}
            onKeyDown={blockNegativeHourKeys}
            error={addHoursOverMax}
            helperText={addHoursOverMax ? 'Hours cannot exceed 24h' : ' '}
          />
          <TextField
            label={<LabelWithIcon icon={<NotesIcon fontSize={ICON_SIZE.sm} />} label="Description (optional)" inline />}
            size="small"
            value={addDescription}
            onChange={(e) => setAddDescription(e.target.value)}
            error={addDescriptionOverLimit}
            helperText={
              addDescriptionOverLimit
                ? `${addDescription.length}/${WORK_LOG_DESCRIPTION_MAX} — ${addDescription.length - WORK_LOG_DESCRIPTION_MAX} characters over the limit`
                : `${addDescription.length}/${WORK_LOG_DESCRIPTION_MAX}`
            }
          />
          <Button variant="outlined" size="small" disabled={!isValidHours(addHours) || addHoursOverMax || addDescriptionOverLimit || addMutation.isPending} onClick={addEntry}>
            {addMutation.isPending ? 'Adding…' : 'Add'}
          </Button>
        </Stack>
      ) : (
        <Tooltip
          title={
            approval?.status === 'Submitted'
              ? 'This project\'s week has been submitted and is awaiting review.'
              : approval?.status === 'Approved'
                ? 'This project\'s week has been approved and locked.'
                : 'Entries older than the previous week cannot be added or edited.'
          }
        >
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Adding entries for this date is disabled.
          </Typography>
        </Tooltip>
      )}
    </Paper>
  );
}
